import { tenantStore } from '../state/tenant.js';

/**
 * API-Client der Shell:
 * - Cookies immer mitschicken (Session-Auth)
 * - X-Tenant-Header aus dem Tenant-Store
 * - Bei 401 EINMAL /auth/refresh versuchen und den Request wiederholen;
 *   parallele 401s teilen sich denselben Refresh (kein Refresh-Sturm).
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** X-Tenant-Header mitsenden (Default: true). */
  tenant?: boolean;
}

/** In Tests austauschbar (statt globalem fetch-Mocking). */
export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;
let fetchImpl: FetchLike = (input, init) => fetch(input, init);
export function setFetchImplementation(impl: FetchLike): void {
  fetchImpl = impl;
}

let refreshPromise: Promise<boolean> | null = null;

function ensureRefreshed(): Promise<boolean> {
  refreshPromise ??= fetchImpl('/api/v1/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

const NO_RETRY_PATHS = new Set(['/api/v1/auth/login', '/api/v1/auth/refresh']);

/**
 * Roh-Antwort mit allem drum herum (Cookies, X-Tenant, 401-Refresh,
 * Fehler → ApiError). Für alles, was kein JSON ist — z. B. der ZIP-Download
 * des Baukastens (v6/E5).
 */
export async function apiRaw(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const doFetch = (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    const tenant = tenantStore.get();
    if (options.tenant !== false && tenant !== null) headers['x-tenant'] = tenant;
    return fetchImpl(path, {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    });
  };

  let response = await doFetch();

  if (response.status === 401 && !NO_RETRY_PATHS.has(path)) {
    if (await ensureRefreshed()) {
      response = await doFetch();
    }
  }

  if (!response.ok) {
    let payload: { message?: string; error?: string } | null = null;
    try {
      payload = (await response.json()) as { message?: string; error?: string };
    } catch {
      // kein JSON-Body — generische Meldung
    }
    throw new ApiError(
      payload?.message ?? `Anfrage fehlgeschlagen (HTTP ${response.status})`,
      response.status,
      payload?.error,
    );
  }

  return response;
}

export async function api<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return (await apiRaw(path, options)).json() as Promise<T>;
}
